/**
 * Internal links in the visitor's market: '/kontakt' → '/en/kontakt', '/koszyk' → '/en/cart'.
 * Pure — used by the client navigation helpers (core/i18n/navigation.tsx), server components and
 * HTML from the editors (rich text, product descriptions).
 */
import { localizePath, type Market } from './markets.ts';
import { localizeRoute } from './routes.ts';

export interface LinkContext {
  locale: string;
  market?: Market | null;
}

/** Same-origin app paths only — not protocol-relative URLs, the API/media/admin or plain files. */
export function isLocalizableHref(href: string): boolean {
  if (!href.startsWith('/') || href.startsWith('//')) return false;
  const pathname = href.split(/[?#]/)[0];
  if (/^\/(api|static|_next|admin|studio|games|smash)(\/|$)/.test(pathname)) return false;
  return !/\.[a-z0-9]{2,5}$/i.test(pathname);
}

/** An app path in `context`'s language and market. Idempotent: a localized path stays as it is. */
export function localizeHref(href: string, context: LinkContext): string {
  if (!isLocalizableHref(href)) return href;
  const { market } = context;
  if (market?.url_type === 'prefix' && market.prefix) {
    const prefix = `/${market.prefix}`;
    if (href === prefix || href.startsWith(`${prefix}/`) || href.startsWith(`${prefix}?`) || href.startsWith(`${prefix}#`)) return href;
  }
  const localized = localizeRoute(context.locale, href);
  return market ? localizePath(market, localized) : localized;
}

/** Rewrites internal href="/..." links inside editor HTML (rich text, descriptions). */
export function localizeHtmlLinks(html: string, context: LinkContext): string {
  if (!html || !html.includes('href')) return html;
  return html.replace(/(\shref=)(["'])(\/[^"']*)\2/gi, (match, attr: string, quote: string, href: string) => {
    const localized = localizeHref(href, context);
    return localized === href ? match : `${attr}${quote}${localized}${quote}`;
  });
}

/** Strips the market's /xx/ prefix: the path the app's routes and comparisons understand. */
export function stripMarketPrefix(pathname: string, market?: Market | null): string {
  if (market?.url_type !== 'prefix' || !market.prefix) return pathname;
  const prefix = `/${market.prefix}`;
  if (pathname === prefix) return '/';
  return pathname.startsWith(`${prefix}/`) ? pathname.slice(prefix.length) : pathname;
}
